import { Link } from 'react-router-dom';
import Footer from '@/pages/home/components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL;
const pageUrl = `${siteUrl}/shopping-guide`;

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Complete CSSBuy Sheet Shopping Guide: How to Use Agent Spreadsheets Like a Pro (2026)',
  description: 'Step-by-step shopping guide covering how to choose the right CSSBuy spreadsheet, navigate categories, understand sizing, place orders, and track your haul. Complete walkthrough for beginners and experienced agent shoppers.',
  author: {
    '@type': 'Person',
    name: 'Shopping Expert',
  },
  datePublished: '2026-07-06',
  dateModified: '2026-07-06',
  url: pageUrl,
  publisher: {
    '@type': 'Organization',
    name: 'CSSSpreadsheet Hub',
    url: siteUrl,
  },
  mainEntityOfPage: {
    '@type': 'WebPage',
    '@id': pageUrl,
  },
};

export default function ShoppingGuidePage() {
  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <nav className="sticky top-0 z-50 bg-background-50/80 backdrop-blur-md border-b border-background-200/70">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between h-14 md:h-16">
            <Link to="/" className="flex items-center gap-2 group cursor-pointer">
              <span className="font-heading text-lg md:text-xl font-semibold text-foreground-950 tracking-tight">
                CSS<span className="text-primary-500">Spreadsheet</span>
              </span>
            </Link>
            <Link
              to="/"
              className="flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>
          </div>
        </div>
      </nav>

      <main>
        <article>
          <header className="bg-accent-50 py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6">
              <span className="inline-block px-3 py-1 bg-background-50 rounded-full text-xs md:text-sm font-medium text-accent-700 mb-4">
                Shopping Guide
              </span>
              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4 leading-tight">
                Complete CSSBuy Sheet Shopping Guide: How to Use Agent Spreadsheets Like a Pro (2026)
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-xs md:text-sm text-foreground-500">
                <span>Updated: July 2026</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>15 min read</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span>Shopping Expert</span>
              </div>
            </div>
          </header>

          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed space-y-6 md:space-y-8">

              <p className="text-lg md:text-xl text-foreground-900 font-medium leading-relaxed">
                Shopping through agent platforms can feel overwhelming if you are just getting started, but the <strong>CSSBuy spreadsheet</strong> system makes it remarkably simple once you understand the workflow. This comprehensive shopping guide walks you through every step — from choosing the right spreadsheet to placing your first order and tracking your haul all the way to your doorstep.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10">
                Step 1: Choose the Right Spreadsheet for Your Needs
              </h2>
              <p>
                Not all spreadsheets are created equal. The best <strong>CSSBuy spreadsheet</strong> for you depends on what you are shopping for and your preferred agent platform. Beginners should start with the general CSSBuy spreadsheet, which covers all 14 categories and is updated weekly by an active community. If you are focused on trendy fashion items specifically, the Kakobuy trending spreadsheet updates faster and surfaces items that are currently popular in the community. Premium shoppers who want guaranteed quality should look at the Superbuy verified spreadsheet, where every item has been manually checked before being added to the list. Budget-conscious shoppers can find incredible value in the Oopbuy spreadsheet, which focuses on affordable finds without sacrificing too much quality.
              </p>
              <p>
                The key is to not limit yourself to just one platform. The most successful agent shoppers cross-reference finds across multiple spreadsheets — an item that appears in a <strong>CSSBuy spreadsheet</strong> might have better pricing on Kakobuy, or a Superbuy-verified find might be available cheaper through Oopbuy. Building this habit early will save you significant money over time.
              </p>

              <div className="bg-accent-50 rounded-lg p-5 md:p-6 my-8">
                <p className="text-sm font-semibold text-accent-800 mb-2">Pro Tip</p>
                <p className="text-sm text-foreground-700">
                  Bookmark at least two spreadsheets from different platforms. Cross-referencing prices and availability between them takes an extra two minutes per item but can save 15-30% on your total haul cost.
                </p>
              </div>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10">
                Step 2: Navigate Categories Like a Power User
              </h2>
              <p>
                The <strong>CSSBuy spreadsheet</strong> is organized into 14 intuitive categories — Shoes, Hoodies and Sweaters, T-Shirts, Jackets, Pants and Shorts, Headwear, Sets, Underwear, Jerseys, Accessories, Bags, Belts, Perfumes, and Other Stuff. When you first open a spreadsheet, resist the urge to scroll randomly through all items. Instead, pick one or two categories that match your immediate shopping goals and focus your browsing there.
              </p>
              <p>
                Within each category, items are typically sorted by popularity or recency. Popular items tend to have more community feedback, which means more sizing notes, quality reviews, and fit pictures. Newer items might be undiscovered gems but carry more risk since fewer people have tested them. A balanced approach is to spend 70% of your browsing time on popular verified items and 30% on newer listings that catch your eye.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10">
                Step 3: Understand Sizing Before You Buy
              </h2>
              <p>
                This is the single most important skill in agent shopping and where most beginners make costly mistakes. Asian sizing (which applies to the vast majority of items in any <strong>CSSBuy spreadsheet</strong>) runs significantly smaller than US and European standards. A Chinese XL is often equivalent to a US Medium or even Small depending on the brand. Every good spreadsheet includes sizing notes from the community, and you should never skip reading them — even if it adds an extra minute to your browsing.
              </p>
              <p>
                When sizing notes are unavailable, the safest approach is to always size up at least once, and for fitted items like slim-cut jackets or tapered pants, size up twice. For shoes, Asian sizes typically run 0.5 to 1.5 sizes smaller than US sizing. If you are between sizes, always go with the larger option — you can add an insole to shoes that are slightly too big, but you cannot stretch shoes that are too small.
              </p>
              <p>
                Many spreadsheets also include direct links to size charts provided by the sellers. Always cross-reference these charts against your own measurements rather than relying on the letter size alone. A measuring tape is a small investment that will save you from receiving items that do not fit.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10">
                Step 4: Place Your Order Through Your Agent
              </h2>
              <p>
                Once you have found items you want from the <strong>CSSBuy spreadsheet</strong>, the ordering process is straightforward. Copy the product link from the spreadsheet, navigate to your chosen agent platform (CSSBuy, Kakobuy, Superbuy, or Oopbuy), and paste the link into the agent's search bar. The agent will display the product page with pricing, available sizes, and shipping estimates. Add the item to your cart, specify your preferred size and color, and proceed to checkout.
              </p>
              <p>
                Most agents offer additional services like quality check photos before shipping. For higher-priced items, always opt for this service — it typically costs only a dollar or two and gives you the opportunity to reject items that do not match the seller's photos before they are shipped internationally. When your entire haul arrives at the agent's warehouse, you can consolidate multiple items into a single shipment to save on international shipping costs.
              </p>

              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10">
                Step 5: Track Your Haul and Build Your Shopping Reputation
              </h2>
              <p>
                International shipping from China typically takes 7-25 business days depending on the shipping method you select. Economy lines are cheaper but slower, while express lines deliver faster at a premium. Once your package ships, most agents provide tracking numbers that you can follow through standard carrier websites. After receiving your items, take a few minutes to update the <strong>CSSBuy spreadsheet</strong> community with your own feedback — sizing notes, quality observations, and photos. This is how the spreadsheet ecosystem stays fresh and useful for everyone.
              </p>

              <div className="bg-primary-50 rounded-lg p-6 md:p-8 my-10 text-center">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                  Ready to Start Shopping?
                </h3>
                <p className="text-sm text-foreground-600 mb-5 max-w-md mx-auto">
                  Head back to our hub and browse through all 14 curated categories to find your next favorite piece.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    to="/"
                    className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to Spreadsheet Hub
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center justify-center gap-2 bg-foreground-950 text-background-50 px-6 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Visit Main Platform
                    <i className="ri-arrow-right-up-line"></i>
                  </a>
                </div>
              </div>

            </div>
          </div>
        </article>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 text-center mb-8">
              Continue Reading
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { title: 'Best CSSBuy Spreadsheet Complete Guide', link: '/cssbuy-spreadsheet-complete-guide' },
                { title: 'CSSBuy vs Kakobuy Spreadsheet Comparison', link: '/cssbuy-vs-kakobuy-spreadsheet' },
                { title: 'Top 10 CSSBuy Spreadsheet Sneaker Finds', link: '/cssbuy-vs-kakobuy-spreadsheet' },
                { title: 'How to Use Agent Shopping Spreadsheets', link: '/shopping-guide' },
              ].map((item) => (
                <Link
                  key={item.title}
                  to={item.link}
                  className="bg-background-50 rounded-lg p-5 hover:bg-primary-50 transition-all duration-300 group cursor-pointer"
                >
                  <h4 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-700 transition-colors mb-2">
                    {item.title}
                  </h4>
                  <span className="text-xs text-primary-600 font-medium group-hover:gap-1.5 transition-all inline-flex items-center gap-1">
                    Read Article
                    <i className="ri-arrow-right-s-line"></i>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}